/**
 * Textes du jeu en français : la langue de référence, qui définit les clés. Une langue ajoutée doit
 * fournir toutes ces clés (le typage de `Messages` le vérifie). Les valeurs peuvent contenir des
 * paramètres `{nom}` ; un objet `{ one, other }` donne un pluriel selon le paramètre `count`.
 *
 * Le point de départ narratif (relais commercial, épave, porte d'une colonie) n'est pas tranché : seuls
 * les textes `tutorial.welcome.*` changeront.
 */
export const fr = {
  "app.title": "Station Keeping",
  "app.loading": "Mise en orbite…",
  "app.canvasLabel": "Plan de la station. Flèches pour se déplacer, Entrée pour choisir une case.",

  "signin.title": "Connexion",
  "signin.codeTitle": "Entre ton code",
  "signin.tagline": "Ta station t'attend en orbite.",
  "signin.hint":
    "Pas de mot de passe : un code à 6 chiffres arrive par e-mail. Nouveau ? Ton compte et ta station naissent à la première connexion.",
  "signin.sending": "Envoi du code…",
  "signin.checking": "Vérification…",
  "signin.resendIn": "Nouveau code dans",
  "signin.guest":
    "Ta station n'est liée à aucune adresse. Pour continuer à jouer, sécurise-la avec ton e-mail : elle te suivra sur tous tes appareils.",
  "signin.studio": "Un jeu du studio Drowzzy",

  "tutorial.label": "Tutoriel",
  "tutorial.progress": "Étape {step} sur {total}",
  "tutorial.skip": "Passer le tuto",
  "tutorial.start": "C'est parti",
  "tutorial.next": "Suivant",
  "tutorial.finish": "Terminer",
  "tutorial.yourTurn": "À toi : fais-le pour continuer.",
  "tutorial.replay": "Revoir le tutoriel",
  "tutorial.welcome.title": "Bienvenue à bord",
  "tutorial.welcome.body":
    "Ce relais commercial tourne autour de la planète depuis des années. Les vaisseaux comptent sur lui pour faire le plein, réparer, se reposer et commercer. À toi de le faire grandir, et de le garder en état de marche.",
  "tutorial.score.title": "Ton score",
  "tutorial.score.body":
    "Les crédits paient tout : bâtiments, rustines, réparations, améliorations. La réputation attire de plus gros vaisseaux, qui paient mieux.",
  "tutorial.level.title": "Ton niveau",
  "tutorial.level.body":
    "Chaque vaisseau bien servi rapporte de l'expérience. En montant de niveau, ta station débloque de nouveaux bâtiments, plus de docks et des améliorations.",
  "tutorial.orbit.title": "Soleil et ombre",
  "tutorial.orbit.body":
    "La frise d'orbite : 4 minutes de soleil, puis 2 minutes dans l'ombre de la planète. Dans l'ombre, les panneaux solaires ne produisent rien : les batteries prennent le relais.",
  "tutorial.openBuild.title": "Construire",
  "tutorial.openBuild.body": "Touche CONSTRUIRE, sur le pupitre, pour ouvrir le menu des bâtiments.",
  "tutorial.pickSolar.title": "Un panneau solaire",
  "tutorial.pickSolar.body": "Choisis le panneau solaire : plus d'énergie tant que le soleil brille.",
  "tutorial.placeSolar.title": "Pose-le",
  "tutorial.placeSolar.body": "Touche une case en surbrillance : elle doit toucher la station. Échap pour annuler.",
  "tutorial.ships.title": "Les vaisseaux",
  "tutorial.ships.body":
    "Une autoroute spatiale passe devant ta station : dès qu'un dock est libre, un vaisseau la quitte pour s'y amarrer, se fait servir et repart en payant. Touche un vaisseau sur le plan pour voir ses besoins.",
  "tutorial.failures.title": "Usure et pannes",
  "tutorial.failures.body":
    "Les modules s'usent, puis tombent en panne. Rustine : rapide et pas chère, mais le module reste fragile. Réparer : plus long, il repart à neuf.",
  "tutorial.offline.title": "Bon vol",
  "tutorial.offline.body": "Ta station tourne même quand tu fermes le jeu : à ton retour, un bilan t'attend.",

  "hud.credits": "Crédits",
  "hud.level": "Niveau",
  "hud.levelValue": "Niv. {level}",
  "hud.levelDetail": "{xp} XP sur {next} pour le niveau {level}",
  "hud.levelMax": "Niveau maximal atteint",
  "hud.reputation": "Réputation",
  "hud.energy": "Énergie",
  "hud.energyDetail": "+{in} / −{out} par seconde · batteries {stored}/{capacity}",
  "hud.oxygen": "Oxygène",
  "hud.oxygenDetail": "+{in} / −{out} par seconde · réserve {percent} %",
  "hud.crew": "Équipage",
  "hud.crewDetail": "{needed} postes à pourvoir pour {crew} membres d'équipage",
  "hud.teams": "Équipes",
  "hud.teamsDetail": "{free} équipe(s) de maintenance libre(s) sur {total}",
  "hud.day": "Soleil",
  "hud.night": "Ombre",
  "hud.nightIn": "ombre dans {time}",
  "hud.dayIn": "soleil dans {time}",
  "hud.condition": "État de la station : {percent} %",
  "hud.conditionShort": "État",
  "hud.shortage.energy": "Énergie insuffisante : des modules sont délestés.",
  "hud.shortage.oxygen": "Réserve d'oxygène vide : le moral baisse.",

  "tab.build": "Construire",
  "tab.failures": "Pannes",
  "tab.ships": "Vaisseaux",
  "tab.station": "Station",
  "panel.close": "Fermer",
  "deck.label": "Pupitre de commandes",
  "deck.count": {
    one: "{count} à voir",
    other: "{count} à voir",
  },

  "module.core.name": "Noyau",
  "module.core.description":
    "Le cœur de la station. Il ne tombe jamais en panne : générateur, oxygène, équipage minimal, une équipe de maintenance et un sas pour les navettes de commerce.",
  "module.dock.name": "Dock",
  "module.dock.description":
    "Un poste d'amarrage : dès qu'il est libre, un vaisseau quitte l'autoroute pour lui. Plus de docks, plus d'escales.",
  "module.solar_panel.name": "Panneau solaire",
  "module.solar_panel.description": "Produit de l'énergie au soleil, rien dans l'ombre de la planète.",
  "module.battery.name": "Batterie",
  "module.battery.description": "Stocke le surplus du jour pour passer l'ombre.",
  "module.o2_recycler.name": "Recycleur d'oxygène",
  "module.o2_recycler.description": "Produit l'oxygène de l'équipage et des équipages à quai.",
  "module.habitat.name": "Habitat",
  "module.habitat.description": "Loge quatre membres d'équipage (un de plus par amélioration), qui font tourner les modules.",
  "module.cantina.name": "Cantina",
  "module.cantina.description": "Repos des équipages de passage. Remonte le moral de son anneau.",
  "module.repair_workshop.name": "Atelier de réparation",
  "module.repair_workshop.description": "Répare les vaisseaux de passage.",
  "module.fuel_depot.name": "Dépôt de carburant",
  "module.fuel_depot.description": "Fait le plein des vaisseaux.",
  "module.warehouse.name": "Entrepôt",
  "module.warehouse.description": "Achète et vend les cargaisons des vaisseaux.",
  "module.maintenance_bay.name": "Baie de maintenance",
  "module.maintenance_bay.description":
    "Une équipe de maintenance de plus, pour traiter deux pannes à la fois. Améliorée, elle accélère toutes les interventions.",

  "zone.weightless": "Apesanteur (anneaux 1 et 2)",
  "zone.gravity": "Gravité (anneaux 3 et plus)",
  "zone.any": "Partout",
  "zone.core": "Centre",
  "zone.core.short": "Centre",
  "zone.weightless.short": "Apesanteur",
  "zone.gravity.short": "Gravité",

  "status.running": "En service",
  "status.building": "En chantier",
  "status.off": "En veille",
  "status.repairing": "En réparation",
  "status.upgrading": "En amélioration",
  "status.stopped": "À l'arrêt",
  "status.unstaffed": "Sans équipage",
  "status.unpowered": "Sans énergie",
  "status.patching": "Rustine en cours",
  "status.jobLeft": "{status}, encore {time}",

  "wear.label": "Usure",
  "wear.ok": "Bon état",
  "wear.worn": "Usé",
  "wear.critical": "Critique",

  "failure.air_leak": "Fuite d'air",
  "failure.overheat": "Surchauffe",
  "failure.short_circuit": "Court-circuit",
  "failure.stage.1": "Rendement réduit",
  "failure.stage.2": "Contamine ses voisins",
  "failure.stage.3": "À l'arrêt",
  "failure.effect.air_leak": "Le moral de l'anneau baisse et l'oxygène fuit.",
  "failure.effect.overheat": "Les modules voisins s'usent plus vite.",
  "failure.effect.short_circuit": "Les batteries se vident.",
  "failure.escalatesIn": "Empire dans {time} si rien n'est fait.",
  "failure.escalatesLabel": "Empire si rien n'est fait, dans",
  "failure.debt": "Chaque minute de panne fait monter la facture.",

  "action.build": "Construire",
  "action.cancel": "Annuler",
  "action.patch": "Rustine",
  "action.repair": "Réparer",
  "action.revise": "Réviser",
  "action.demolish": "Démanteler",
  "action.demolishConfirm": "Démanteler · +{amount} cr",
  "action.toggleOff": "Mettre en veille",
  "action.toggleOn": "Remettre en service",
  "action.extendRing": "Ouvrir l'anneau {ring}",
  "action.patch.hint": "Rapide et pas chère : le module repart, mais reste usé et plus fragile.",
  "action.repair.hint": "Le module est immobilisé le temps des travaux, puis remis à neuf.",
  "action.revise.hint": "Remet à neuf un module usé avant qu'il ne tombe en panne.",
  "action.duration": "{time}",
  "action.cost": "{amount} cr",

  "build.title": "Construire",
  "build.hint": "Choisis un module, puis une case en surbrillance.",
  "build.placing": "Place : {module}",
  "build.placingHint": "Touche une case en surbrillance. Échap pour annuler.",
  "build.noCellHint": "Aucune case possible pour l'instant : il lui faut une case libre qui touche la station, zone {zone}.",
  "build.price": "{amount} cr",
  "build.power": "Énergie {value}",
  "build.crew": "Équipage {value}",
  "build.noCell": "Aucune case libre pour ce module.",
  "build.ringsTitle": "Agrandir la station",
  "build.ringsHint": "Les anneaux extérieurs ouvrent de la place, avec gravité.",
  "build.allRingsOpen": "Tous les anneaux sont ouverts.",
  "build.built": "{module} en chantier.",
  "build.locked": "Niveau {level}",
  "build.lockedHint": "Se débloque au niveau {level} de la station.",
  "build.limit": "{count}/{limit}",
  "build.limitHint": "Au plus {limit} à ce niveau ; {next} au niveau {level}.",
  "build.limitMax": "Au plus {limit}.",
  "build.ringLocked": "Anneau {ring} : niveau {level} requis.",

  "failures.title": "Pannes",
  "failures.none": "Aucune panne. La station ronronne.",
  "failures.teams": {
    one: "{free} équipe libre sur {total}",
    other: "{free} équipes libres sur {total}",
  },
  "failures.triageHint": "Une intervention occupe une équipe : choisis ce qui presse.",
  "failures.watchTitle": "À surveiller",
  "failures.watchHint":
    "Usés, ils tiennent encore ; critiques, ils peuvent lâcher à tout moment. Une révision les remet à neuf et rapporte de l'expérience.",
  "failures.since": "depuis {time}",

  "ships.title": "Vaisseaux",
  "ships.none": "Aucun vaisseau en vue.",
  "ships.highway": "L'autoroute spatiale ne s'arrête jamais : un dock libre attire aussitôt un vaisseau.",
  "ships.legend": "Touche un vaisseau pour sa fiche. Chaque jauge est un besoin : elle se remplit quand ta station le sert.",
  "ships.berths": {
    one: "{used} poste occupé sur {total}",
    other: "{used} postes occupés sur {total}",
  },
  "ship.shuttle": "Navette",
  "ship.freighter": "Cargo",
  "ship.liner": "Long-courrier",
  "ship.phase.highway": "Sur l'autoroute",
  "ship.phase.approach": "En approche",
  "ship.phase.docked": "À quai",
  "ship.phase.leaving": "Repart",
  "ship.patience": "Patience",
  "ship.leavesIn": "Repart dans",
  "ship.leavesInTime": "Repart dans {time}",
  "ship.patienceOnArrival": "Patience, décomptée une fois à quai",
  "ship.patienceHint":
    "À zéro, il repart, servi ou non. Bien servi, il paie plus, ta réputation monte et ta station gagne de l'expérience.",
  "ship.headingToCore": "Vient vers le sas du noyau",
  "ship.headingTo": "Vient vers : {module}, anneau {ring}",
  "ship.dockedAtCore": "À quai, au sas du noyau",
  "ship.dockedAt": "À quai : {module}, anneau {ring}",
  "ship.needs": "Ses besoins",
  "ship.needsHint":
    "Chaque jauge se remplit quand ta station le sert. Sur le plan, les pictos au-dessus du vaisseau montrent ce qui lui manque.",
  "ship.need.done": "Servi.",
  "ship.need.nobody": "Personne ne le sert ici. À construire : {module}.",
  "ship.need.onArrival": "Servi une fois à quai.",
  "ship.need.serving": "En cours : servi d'ici {time} environ.",
  "ship.need.amount": "{served} sur {asked} unités",
  "ship.payment": "Paiement au départ",
  "ship.payNow": "S'il repartait maintenant",
  "ship.payFull": "Servi en entier, pourboire compris",
  "need.fuel": "Carburant",
  "need.repair": "Réparation",
  "need.rest": "Repos",
  "need.trade": "Commerce",
  "ship.noService": "Personne ne sert ce besoin ici.",

  "station.title": "Station",
  "station.stats": "Bilan",
  "station.stat.earned": "Crédits gagnés",
  "station.stat.served": "Vaisseaux bien servis",
  "station.stat.partial": "Vaisseaux mal servis",
  "station.stat.failures": "Pannes",
  "station.stat.repairs": "Réparations",
  "station.stat.patches": "Rustines",
  "station.stat.age": "Âge de la station",
  "station.stat.upgrades": "Améliorations",
  "station.level": "Progression",

  "module.ring": "Anneau {ring}",
  "module.place": "Anneau {ring} · {zone}",
  "module.efficiency": "Rendement {percent} %",
  "module.level": "Niveau {level}/{max}",
  "module.about": "Le module",
  "module.controls": "Commandes du module",

  "event.failure": "Panne : {module} ({failure}).",
  "event.escalation": "{module} : {stage}.",
  "event.repaired": "{module} est remis à neuf.",
  "event.patched": "{module} repart (rustine).",
  "event.built": "{module} est en service.",
  "event.upgraded": "{module} passe au niveau {level}.",
  "event.payment": "+{amount} cr",

  "error.generic": "Action impossible.",
  "error.NOT_ENOUGH_CREDITS": "Pas assez de crédits.",
  "error.CELL_OCCUPIED": "Cette case est déjà prise.",
  "error.NOT_CONNECTED": "Le module doit toucher la station.",
  "error.WRONG_ZONE": "Ce module ne peut pas aller sur cet anneau.",
  "error.RING_CLOSED": "Cet anneau n'est pas encore ouvert.",
  "error.OUT_OF_GRID": "Hors de la station.",
  "error.WOULD_DISCONNECT": "Sans ce module, une partie de la station serait isolée.",
  "error.DOCK_OCCUPIED": "Un vaisseau est amarré à ce dock.",
  "error.CORE_LOCKED": "Le noyau ne se touche pas.",
  "error.NO_FREE_TEAM": "Toutes les équipes de maintenance sont occupées.",
  "error.MODULE_BUSY": "Une intervention est déjà en cours.",
  "error.NOT_FAILED": "Ce module n'est pas en panne.",
  "error.NOTHING_TO_REPAIR": "Ce module est à neuf.",
  "error.UNDER_CONSTRUCTION": "Le module est encore en chantier.",
  "error.MAX_RING_REACHED": "Tous les anneaux sont ouverts.",
  "error.MODULE_LOCKED": "Ce module se débloque à un niveau plus élevé.",
  "error.MODULE_LIMIT_REACHED": "Limite atteinte pour ce module à ce niveau.",
  "error.RING_LOCKED": "La station doit monter de niveau pour ouvrir cet anneau.",
  "error.MODULE_FAILED": "Répare d'abord ce module.",
  "error.MAX_LEVEL_REACHED": "Ce module est au niveau maximal.",
  "error.LEVEL_TOO_LOW": "La station doit d'abord monter de niveau.",

  "level.title": "Niveau {level}",
  "level.xp": "{xp} / {next} XP",
  "level.max": "Niveau maximal : ta station a tout débloqué. Les améliorations continuent.",
  "level.howTo":
    "Chaque vaisseau bien servi rapporte de l'expérience, davantage pour les gros. Une révision faite avant la panne aussi.",
  "level.next": "Au niveau {level}",
  "level.up.title": "Niveau {level} !",
  "level.up.body": "Ta station gagne en renommée : de nouvelles possibilités s'ouvrent.",
  "level.up.close": "En avant",
  "level.unlock.module": "Nouveau : {module}",
  "level.unlock.limit": "{module} : jusqu'à {count}",
  "level.unlock.ring": "Anneau {ring} : à ouvrir contre des crédits",
  "level.unlock.upgrades": "Améliorations jusqu'au niveau {level}",
  "level.why.fuel_depot": "Les cargos s'arrêtent désormais : ils veulent du carburant en plus du commerce.",
  "level.why.maintenance_bay": "Une deuxième équipe de maintenance : deux pannes traitées à la fois.",
  "level.why.cantina": "Les long-courriers arrivent : ils veulent se reposer, et paient bien.",
  "level.why.repair_workshop": "Les vaisseaux abîmés peuvent se faire réparer chez toi.",

  "upgrade.title": "Amélioration",
  "upgrade.action": "Améliorer (niv. {level})",
  "upgrade.hint": "Le module s'arrête le temps des travaux, sans occuper d'équipe de maintenance.",
  "upgrade.inProgress": "Amélioration en cours",
  "upgrade.max": "Niveau maximal atteint.",
  "upgrade.needLevel": "La station doit atteindre le niveau {level} pour aller plus loin.",
  "upgrade.gain.power": "Énergie produite",
  "upgrade.gain.battery": "Capacité de stockage",
  "upgrade.gain.oxygen": "Oxygène produit",
  "upgrade.gain.crewCapacity": "Logement",
  "upgrade.gain.service": "Service par seconde",
  "upgrade.gain.payment": "Paiement des vaisseaux",
  "upgrade.gain.speed": "Interventions plus courtes de",
  "upgrade.gain.wear": "Usure ralentie de",

  "sandbox.badge": "Bac à sable",
  "sandbox.speed": "Vitesse ×{speed}",

  "sync.resynced": "Station resynchronisée avec le serveur.",
  "sync.offline": "Hors ligne",
  "sync.offlineHint": "La partie continue ; la sauvegarde reprendra au retour de la connexion.",
  "sync.reopening": "Synchronisation…",

  "blocked.offline.title": "Pas de liaison avec la station",
  "blocked.offline.body": "Le serveur ne répond pas. Nouvel essai dans quelques secondes.",
  "blocked.upgrade.title": "Mise à jour nécessaire",
  "blocked.upgrade.body": "Cette version du jeu n'est plus prise en charge. Recharge la page pour passer à la dernière.",
  "blocked.suspended.title": "Compte suspendu",
  "blocked.suspended.body": "Ce compte est suspendu. Écris-nous si tu penses qu'il s'agit d'une erreur.",
  "blocked.unavailable.title": "Station en maintenance",
  "blocked.unavailable.body": "Nous remettons ta station en état. Réessaie un peu plus tard.",
  "blocked.error.title": "Liaison interrompue",
  "blocked.error.body": "Une erreur inattendue a interrompu la partie.",
  "blocked.retry": "Réessayer",
  "blocked.reload": "Recharger la page",

  "takeover.device.title": "Station ouverte ailleurs",
  "takeover.device.body": "Ta station est ouverte sur un autre appareil. Une seule partie à la fois : celle-ci est en pause.",
  "takeover.tab.title": "Station ouverte dans un autre onglet",
  "takeover.tab.body": "Un autre onglet de ce navigateur a pris la main. Celui-ci est en pause.",
  "takeover.resume": "Reprendre ici",

  "offline.title": "Pendant ton absence",
  "offline.away": "Absence : {time}.",
  "offline.capped": "Les gains s'arrêtent à {time} d'absence.",
  "offline.credits": "Crédits gagnés",
  "offline.ships": "Vaisseaux bien servis",
  "offline.xp": "Expérience gagnée",
  "offline.level": "Niveau atteint",
  "offline.builds": "Chantiers terminés",
  "offline.jobs": "Interventions terminées",
  "offline.job.patch": "{module} : rustine posée",
  "offline.job.repair": "{module} : remis à neuf",
  "offline.watch": "À surveiller",
  "offline.watchHint": "Usés ou en panne : passe les voir.",
  "offline.quiet": "Rien de notable : la station a tourné sans toi.",
  "offline.close": "Reprendre les commandes",

  "update.available": "Une nouvelle version du jeu est prête.",
  "update.reload": "Mettre à jour",

  "account.title": "Ton compte",
  "account.signedInAs": "Connecté avec",
  "account.expired": "Ta session a expiré. Connecte-toi pour retrouver ta station.",
  "account.closed": "La connexion par e-mail est fermée pour le moment.",
  "account.email": "Adresse e-mail",
  "account.emailPlaceholder": "toi@exemple.fr",
  "account.emailHint": "Nous t'envoyons un code à 6 chiffres. Pas de mot de passe.",
  "account.sendCode": "Recevoir un code",
  "account.code": "Code reçu par e-mail",
  "account.codeSent": "Code envoyé à {email}, valable 10 minutes.",
  "account.verify": "Valider",
  "account.resend": "Renvoyer un code",
  "account.resendIn": "Nouveau code possible dans {time}",
  "account.changeEmail": "Changer d'adresse",
  "account.cancel": "Annuler",
  "account.choiceTitle": "Deux stations, une seule à garder",
  "account.choiceTitleShort": "Deux stations",
  "account.choiceBody": "Ce compte a déjà sa station, et cet appareil aussi. Choisis celle que tu gardes : l'autre sera supprimée.",
  "account.choiceCurrent": "Station de cet appareil",
  "account.choiceAccount": "Station du compte",
  "account.choiceStats": "{credits} cr · réputation {reputation} · {modules} modules",
  "account.choiceAge": "{time} de jeu",
  "account.keep": "Garder celle-ci",
  "account.keepConfirm": "Garder celle-ci et supprimer l'autre",
  "account.keepWarning": "L'autre station sera perdue, sans retour possible.",
  "account.logout": "Se déconnecter",
  "account.logoutHint": "Ta station reste sur ton compte : tu la retrouveras en te reconnectant.",
  "account.delete": "Supprimer mon compte",
  "account.deleteWarning": "Ton compte et ta station seront effacés tout de suite, sans retour possible.",
  "account.deleteConfirm": "Supprimer définitivement",
  "account.deleted": "C'est fait : tout a été effacé.",

  "apiError.generic": "Une erreur est survenue. Réessaie.",
  "apiError.network": "Pas de liaison avec le serveur. Vérifie ta connexion et réessaie.",
  "apiError.VALIDATION_FAILED": "Vérifie ce que tu as saisi.",
  "apiError.TOO_MANY_REQUESTS": "Trop d'essais rapprochés : patiente un peu.",
  "apiError.CODE_INVALID": "Code incorrect.",
  "apiError.CODE_INVALID.left": {
    one: "Code incorrect : encore {count} essai.",
    other: "Code incorrect : encore {count} essais.",
  },
  "apiError.CODE_EXPIRED": "Ce code a expiré : demandes-en un nouveau.",
  "apiError.CODE_ATTEMPTS_EXCEEDED": "Trop d'essais : demande un nouveau code.",
  "apiError.CODE_RESEND_TOO_SOON": "Patiente un peu avant de redemander un code.",
  "apiError.EMAIL_RATE_LIMITED": "Trop de codes demandés pour cette adresse : réessaie plus tard.",
  "apiError.EMAIL_LOGIN_CLOSED": "La connexion par e-mail est fermée pour le moment.",
  "apiError.MAIL_UNAVAILABLE": "L'e-mail n'a pas pu partir. Réessaie dans un instant.",
  "apiError.BOT_CHECK_FAILED": "Vérification anti-robot échouée : réessaie.",
  "apiError.BOT_CHECK_UNAVAILABLE": "Vérification anti-robot indisponible : réessaie dans un instant.",
  "apiError.CHOICE_EXPIRED": "Ce choix n'est plus valable : reconnecte-toi.",
  "apiError.ACCOUNT_SUSPENDED": "Ce compte est suspendu.",
  "apiError.GUEST_SIGNUPS_CLOSED": "Les nouvelles stations sont fermées pour le moment : réessaie plus tard.",

  "loginLink.title": "Changer de compte ?",
  "loginLink.body": "Ce lien connecte {email} sur cet appareil. Ta station actuelle reste sur ton compte.",
  "loginLink.confirm": "Se connecter",

  "time.seconds": "{s} s",
  "time.minutes": "{m} min {s} s",
  "time.hours": "{h} h {m} min",
  "time.days": {
    one: "{count} jour",
    other: "{count} jours",
  },
} as const;
